import axios from 'axios';

const getCsrfToken = () => {
  const name = 'csrftoken';
  const cookieValue = document.cookie.split('; ').find(row => row.startsWith(name))?.split('=')[1];
  return cookieValue;
};

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

// Crea una instancia de Axios con configuración global
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor de request: se ejecuta antes de cada petición para agregar los headers
apiClient.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem('access');
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    config.headers['X-CSRFToken'] = getCsrfToken();
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor de response para manejar errores 401 y refrescar el token
apiClient.interceptors.response.use(
  response => response,
  async (error) => {
    const originalRequest = error.config;
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('refresh');
      if (refreshToken) {
        try {
          const { data } = await axios.post(
            `${API_BASE_URL}/users/token/refresh/`,
            { refresh: refreshToken },
            {
              headers: {
                'X-CSRFToken': getCsrfToken(),
              },
              withCredentials: true,
            }
          );
          localStorage.setItem('access', data.access);
          if (data.refresh) {
            localStorage.setItem('refresh', data.refresh);
          }
          originalRequest.headers['Authorization'] = `Bearer ${data.access}`;
          return apiClient(originalRequest);
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      }
    }
    return Promise.reject(error);
  }
);

// --- Funciones de la API ---

export const ensureCsrfToken = async () => {
  await apiClient.get('/users/csrf/');
};

// Funciones para el usuario

export const registerUser = async (document, email, full_name, password) => {
  await ensureCsrfToken();
  const response = await apiClient.post('/users/register/', {
    document,
    email,
    full_name,
    password,
  });
  return response.data;
};

export const loginUser = async (document, password) => {
  await ensureCsrfToken();
  const response = await apiClient.post('/users/login/', {
    document,
    password,
  });
  if (response.data.access) {
    localStorage.setItem('access', response.data.access);
  }
  if (response.data.refresh) {
    localStorage.setItem('refresh', response.data.refresh);
  }
  return response.data;
};

export const logoutUser = async (refreshToken, accessToken) => {
  await apiClient.post('/users/logout/', { refresh: refreshToken }, {
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  });
};

export const getUserDetails = async (token) => {
  const response = await apiClient.get('/users/info/', {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
  return response.data;
};

export const getUserProfile = async (token) => {
  const response = await apiClient.get('/users/profile/', {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
  return response.data;
};

export const updateUserProfile = async (token, formData) => {
  try {
    const response = await apiClient.patch('/users/profile/', formData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const sendPasswordResetEmail = async (email) => {
  await ensureCsrfToken();
  const response = await apiClient.post('/users/reset-password/', {
    email,
  });
  return response.data;
};

export const changePassword = async (token, currentPassword, newPassword) => {
  try {
    const response = await apiClient.post('/users/change-password/', {
      current_password: currentPassword,
      new_password: newPassword
    }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Business Plans API
export const listPlans = async (page = 1) => {
  const { data } = await apiClient.get(`/plans/?page=${page}`);
  return data;
};

export const getPlan = async (slug) => {
  const { data } = await apiClient.get(`/plans/${slug}/`);
  return data;
};

export const createPlan = async (payload) => {
  const { data } = await apiClient.post(`/plans/`, payload);
  return data;
};

export const updatePlan = async (slug, payload) => {
  const { data } = await apiClient.put(`/plans/${slug}/`, payload);
  return data;
};

export const deletePlan = async (slug) => {
  const { data } = await apiClient.delete(`/plans/${slug}/`);
  return data;
};

export const suggestSection = async (slug, section, text) => {
  const { data } = await apiClient.post(`/plans/${slug}/ai/suggest/`, { section, text });
  return data;
};

/**
 * Llama al endpoint SSE de sugerencias y dispara callbacks por evento.
 * @param {string}   slug
 * @param {string}   section  - Nombre del bloque BMC
 * @param {string}   text     - Texto escrito por el usuario
 * @param {Function} onChunk     - (text: string) => void  — fragmento recibido
 * @param {Function} onFallback  - (data: object) => void  — sin API / error
 * @param {Function} onDone      - () => void              — stream completo
 * @param {Function} onError     - (err: Error) => void    — error de red
 */
export const suggestSectionStream = async (
  slug, section, text, onChunk, onFallback, onDone, onError
) => {
  const accessToken = localStorage.getItem('access');
  const csrfToken = getCsrfToken();

  let fetchResponse;
  try {
    fetchResponse = await fetch(`${API_BASE_URL}/plans/${slug}/ai/suggest-stream/`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
        ...(csrfToken   ? { 'X-CSRFToken': csrfToken }               : {}),
      },
      body: JSON.stringify({ section, text }),
    });
  } catch (networkErr) {
    onError?.(networkErr);
    return;
  }

  if (!fetchResponse.ok) {
    // Si es 429, intentar parsear el error para dar más información
    if (fetchResponse.status === 429) {
      try {
        const errorData = await fetchResponse.json();
        const error = new Error(errorData.detail || 'Límite de sugerencias alcanzado');
        error.response = { status: 429, data: errorData };
        onError?.(error);
        return;
      } catch (parseErr) {
        onError?.(new Error(`HTTP ${fetchResponse.status}`));
        return;
      }
    }
    onError?.(new Error(`HTTP ${fetchResponse.status}`));
    return;
  }

  const reader = fetchResponse.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop(); // último fragmento posiblemente incompleto

      for (const part of parts) {
        const line = part.trim();
        if (!line.startsWith('data: ')) continue;
        const payload = JSON.parse(line.slice(6));

        if (payload.type === 'chunk')    { onChunk?.(payload.text); }
        if (payload.type === 'done')     { onDone?.(); }
        if (payload.type === 'fallback') { onFallback?.(payload); }
      }
    }
  } catch (streamErr) {
    onError?.(streamErr);
  }
};

export const actionPlan = async (slug) => {
  const { data } = await apiClient.post(`/plans/${slug}/ai/action-plan/`, {});
  return data;
};

export const getGuideTips = async (section) => {
  const { data } = await apiClient.get(`/plans/guide-tips/`, { params: { section } });
  return data;
};

export const getUsageLimits = async (slug) => {
  const { data } = await apiClient.get(`/plans/${slug}/ai/usage-limits/`);
  return data;
};

// Pitch profesional (comunidad ICETEX)
export const getPitchQuestions = async () => {
  const { data } = await apiClient.get('/plans/pitch/questions/');
  return data;
};

export const getPitchGuideTips = async (section, audience) => {
  const { data } = await apiClient.get('/plans/pitch/guide-tips/', {
    params: { section, audience },
  });
  return data;
};

export const suggestPitchSectionStream = async (
  slug, section, text, audience, communityTags, onChunk, onFallback, onDone, onError
) => {
  const accessToken = localStorage.getItem('access');
  const csrfToken = getCsrfToken();

  let fetchResponse;
  try {
    fetchResponse = await fetch(`${API_BASE_URL}/plans/${slug}/ai/pitch/suggest-stream/`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
        ...(csrfToken ? { 'X-CSRFToken': csrfToken } : {}),
      },
      body: JSON.stringify({
        section,
        text,
        audience,
        community_tags: Array.isArray(communityTags) ? communityTags : [],
      }),
    });
  } catch (networkErr) {
    onError?.(networkErr);
    return;
  }

  if (!fetchResponse.ok) {
    if (fetchResponse.status === 429) {
      try {
        const errorData = await fetchResponse.json();
        const error = new Error(errorData.detail || 'Límite de sugerencias alcanzado');
        error.response = { status: 429, data: errorData };
        onError?.(error);
        return;
      } catch (parseErr) {
        onError?.(new Error(`HTTP ${fetchResponse.status}`));
        return;
      }
    }
    onError?.(new Error(`HTTP ${fetchResponse.status}`));
    return;
  }

  const reader = fetchResponse.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop();

      for (const part of parts) {
        const line = part.trim();
        if (!line.startsWith('data: ')) continue;
        const payload = JSON.parse(line.slice(6));

        if (payload.type === 'chunk') { onChunk?.(payload.text); }
        if (payload.type === 'done') { onDone?.(); }
        if (payload.type === 'fallback') { onFallback?.(payload); }
      }
    }
  } catch (streamErr) {
    onError?.(streamErr);
  }
};

export const generateFullPitch = async (slug, payload) => {
  const { data } = await apiClient.post(`/plans/${slug}/ai/pitch/full/`, payload);
  return data;
};

// PMV — Validación de Hipótesis

export const getPmvQuestions = async () => {
  const { data } = await apiClient.get('/plans/pmv/questions/');
  return data;
};

export const getPmvGuideTips = async (section) => {
  const { data } = await apiClient.get('/plans/pmv/guide-tips/', { params: { section } });
  return data;
};

export const suggestPmvSectionStream = async (
  slug, section, text, onChunk, onFallback, onDone, onError
) => {
  const accessToken = localStorage.getItem('access');
  const csrfToken = getCsrfToken();

  let fetchResponse;
  try {
    fetchResponse = await fetch(`${API_BASE_URL}/plans/${slug}/ai/pmv/suggest-stream/`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
        ...(csrfToken ? { 'X-CSRFToken': csrfToken } : {}),
      },
      body: JSON.stringify({ section, text }),
    });
  } catch (networkErr) {
    onError?.(networkErr);
    return;
  }

  if (!fetchResponse.ok) {
    if (fetchResponse.status === 429) {
      try {
        const errorData = await fetchResponse.json();
        const error = new Error(errorData.detail || 'Límite de sugerencias alcanzado');
        error.response = { status: 429, data: errorData };
        onError?.(error);
        return;
      } catch (parseErr) {
        onError?.(new Error(`HTTP ${fetchResponse.status}`));
        return;
      }
    }
    onError?.(new Error(`HTTP ${fetchResponse.status}`));
    return;
  }

  const reader = fetchResponse.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop();

      for (const part of parts) {
        const line = part.trim();
        if (!line.startsWith('data: ')) continue;
        const payload = JSON.parse(line.slice(6));

        if (payload.type === 'chunk') { onChunk?.(payload.text); }
        if (payload.type === 'done') { onDone?.(); }
        if (payload.type === 'fallback') { onFallback?.(payload); }
      }
    }
  } catch (streamErr) {
    onError?.(streamErr);
  }
};

export const generateFullValidation = async (slug, payload) => {
  const { data } = await apiClient.post(`/plans/${slug}/ai/pmv/full/`, payload);
  return data;
};

// Oportunidades — Mapa de Oportunidades

export const getOppQuestions = async () => {
  const { data } = await apiClient.get('/plans/opp/questions/');
  return data;
};

export const getOppGuideTips = async (section) => {
  const { data } = await apiClient.get('/plans/opp/guide-tips/', { params: { section } });
  return data;
};

export const suggestOppSectionStream = async (
  slug, section, text, onChunk, onFallback, onDone, onError
) => {
  const accessToken = localStorage.getItem('access');
  const csrfToken = getCsrfToken();

  let fetchResponse;
  try {
    fetchResponse = await fetch(`${API_BASE_URL}/plans/${slug}/ai/opp/suggest-stream/`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
        ...(csrfToken ? { 'X-CSRFToken': csrfToken } : {}),
      },
      body: JSON.stringify({ section, text }),
    });
  } catch (networkErr) {
    onError?.(networkErr);
    return;
  }

  if (!fetchResponse.ok) {
    if (fetchResponse.status === 429) {
      try {
        const errorData = await fetchResponse.json();
        const error = new Error(errorData.detail || 'Límite de sugerencias alcanzado');
        error.response = { status: 429, data: errorData };
        onError?.(error);
        return;
      } catch (parseErr) {
        onError?.(new Error(`HTTP ${fetchResponse.status}`));
        return;
      }
    }
    onError?.(new Error(`HTTP ${fetchResponse.status}`));
    return;
  }

  const reader = fetchResponse.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop();

      for (const part of parts) {
        const line = part.trim();
        if (!line.startsWith('data: ')) continue;
        const payload = JSON.parse(line.slice(6));

        if (payload.type === 'chunk') { onChunk?.(payload.text); }
        if (payload.type === 'done') { onDone?.(); }
        if (payload.type === 'fallback') { onFallback?.(payload); }
      }
    }
  } catch (streamErr) {
    onError?.(streamErr);
  }
};

export const generateFullOppMap = async (slug, payload) => {
  const { data } = await apiClient.post(`/plans/${slug}/ai/opp/full/`, payload);
  return data;
};

export default apiClient;